import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { authService } from "../services/authService";
import Logo from "../Image/Logo.jpg";

interface ForgotPasswordError {
  message: string;
  field?: string;
}

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ForgotPasswordError | null>(null);
  const [success, setSuccess] = useState(false);
  
  // Animation states - no splash screen for forgot password
  const [animationComplete, setAnimationComplete] = useState(false);
  const [loginContainerVisible, setLoginContainerVisible] = useState(false);

  useEffect(() => {
    const sequence = async () => {
      // Start animations immediately without splash screen
      setTimeout(() => {
        setAnimationComplete(true);
      }, 200);
      
      setTimeout(() => {
        setLoginContainerVisible(true);
      }, 400);
    };
    
    sequence();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(false);
    
    try {
      const response = await authService.forgotPassword(email);
      
      if (response.success) {
        setSuccess(true);
      } else {
        setError({
          message: response.message || 'Failed to send reset email. Please try again.',
          field: 'email'
        });
      }
    } catch (err: any) {
      console.error('Forgot password error:', err);
      
      // The axios interceptor returns error object directly with message property
      // Handle validation errors with errors array
      let errorMessage = 'Request failed. Please try again.';
      if (err?.errors && Array.isArray(err.errors) && err.errors.length > 0) {
        errorMessage = err.errors.join(', ');
      } else if (err?.message) {
        errorMessage = err.message;
      }
      
      setError({
        message: errorMessage,
        field: err?.statusCode === 404 || err?.statusCode === 400 ? 'email' : undefined
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: 'var(--bg-main)' }}>
      {/* Main Layout */}
      <div className="min-h-screen flex" style={{
        opacity: animationComplete ? 1 : 0,
        transform: animationComplete ? 'scale(1)' : 'scale(0.98)',
        transition: 'all 600ms cubic-bezier(0.25, 0.46, 0.45, 0.94)'
      }}>
        {/* Left Section - Brand */}
        <div className="hidden lg:flex lg:w-[42%] relative overflow-hidden bg-brand-900 text-white">
          <div className="absolute inset-0 surface-grid opacity-20" />
          <div className="relative z-10 flex flex-col justify-between w-full p-10 xl:p-12">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-md bg-white p-1.5">
                <img src={Logo} alt="Logo" className="h-full w-full object-contain" />
              </div>
              <div>
                <p className="text-sm font-extrabold">FleetWMS</p>
                <p className="text-[10px] uppercase tracking-[0.16em] text-white/50 font-bold">Account Recovery</p>
              </div>
            </div>
            <div>
              <h2 className="text-4xl font-extrabold mb-4 leading-tight">Reset access<br />securely</h2>
              <p className="text-white/65 text-base leading-relaxed max-w-md">
                Enter your email and we will send a secure link to restore your console access.
              </p>
            </div>
            <p className="text-xs text-white/35">Encrypted recovery · Time-limited tokens</p>
          </div>
        </div>

        {/* Right Section - Forgot Password Form */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-8" style={{ 
          background: 'var(--bg-main)',
          opacity: loginContainerVisible ? 1 : 0,
          transform: loginContainerVisible ? 'translateY(0)' : 'translateY(100%)',
          transition: 'all 800ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          transitionDelay: '0ms'
        }}>
          <div className="w-full max-w-md">
            <div className="bg-white rounded-lg shadow-panel p-6 sm:p-8 border border-ink-200" style={{ 
              opacity: loginContainerVisible ? 1 : 0,
              transform: loginContainerVisible ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.95)',
              transition: 'all 700ms cubic-bezier(0.34, 1.56, 0.64, 1)',
              transitionDelay: loginContainerVisible ? '200ms' : '0ms'
            }}>
              {/* Header */}
              <div className="mb-6">
                <h2 className="text-2xl font-extrabold mb-1 text-ink-900">Forgot Password</h2>
                <p className="text-sm text-ink-500">Enter your email to reset your password</p>
              </div>

              {/* Success Message */}
              {success && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg animate-fade-in">
                  <div className="flex items-center text-green-700">
                    <i className="fas fa-check-circle mr-2"></i>
                    <span className="text-sm">
                      Password reset link has been sent to your email address.
                    </span>
                  </div>
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg animate-fade-in">
                  <div className="flex items-center text-red-700">
                    <i className="fas fa-exclamation-circle mr-2"></i>
                    <span className="text-sm">{error.message}</span>
                  </div>
                </div>
              )}

              {!success ? (
                <>
                  {/* Forgot Password Form */}
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Email Field */}
                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                        Email Address
                      </label>
                      <input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-all duration-200"
                        style={{ 
                          borderColor: error?.field === 'email' ? '#ef4444' : 'var(--border-color)',
                          background: error?.field === 'email' ? '#fef2f2' : 'white',
                          color: 'var(--text-primary)'
                        }}
                        placeholder="Enter your email"
                      />
                    </div>

                    {/* Send Reset Link Button */}
                    <button
                      type="submit"
                      disabled={isLoading || !email}
                      className="w-full text-white font-semibold py-3 px-4 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed btn-modern"
                      style={{ 
                        background: isLoading || !email ? '#94a3b8' : '#1e3a5f',
                      }}
                    >
                      {isLoading ? (
                        <div className="flex items-center justify-center">
                          <i className="fas fa-spinner fa-spin mr-3"></i>
                          Sending reset link...
                        </div>
                      ) : (
                        <>
                          <i className="fas fa-paper-plane mr-2"></i>
                          Send Reset Link
                        </>
                      )}
                    </button>
                  </form>

                  {/* Back to Login Link */}
                  <div className="text-center mt-6">
                    <Link 
                      to="/login" 
                      className="text-sm font-medium transition-colors hover:underline"
                      style={{ color: '#1e3a5f' }}
                    >
                      <i className="fas fa-arrow-left mr-2"></i>
                      Back to Login
                    </Link>
                  </div>
                </>
              ) : (
                <>
                  {/* Success Actions */}
                  <div className="space-y-4">
                    <p className="text-center text-sm" style={{ color: 'var(--text-secondary)' }}>
                      Didn't receive the email? Check your spam folder or try again.
                    </p>
                    
                    <button
                      onClick={() => {
                        setSuccess(false);
                        setEmail("");
                        setError(null);
                      }}
                      className="w-full text-brand-600 border-2 border-brand-600 font-semibold py-3 px-4 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 hover:bg-brand-50"
                    >
                      <i className="fas fa-redo mr-2"></i>
                      Send Another Link
                    </button>
                    
                    <div className="text-center">
                      <Link 
                        to="/login" 
                        className="text-sm font-medium transition-colors hover:underline"
                        style={{ color: '#1e3a5f' }}
                      >
                        <i className="fas fa-arrow-left mr-2"></i>
                        Back to Login
                      </Link>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}